import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect } from 'react';
import { 
  User, 
  Settings, 
  CreditCard, 
  Shield, 
  Loader2, 
  ArrowLeft, 
  AlertTriangle, 
  Calendar,
  ChevronRight
} from 'lucide-react';
import { profileService } from '@/services/profile.service';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { subscriptionService } from '@/services/subscription.service';
import { format } from 'date-fns';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCredits } from '@/contexts/CreditContext';

export function ProfilePage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, setUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [cancelDialog, setCancelDialog] = useState(false);
  
  // Get credit information from context
  const { creditInfo, isLoading: creditsLoading, refetch: refetchCredits } = useCredits();

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: profileService.getProfile,
    initialData: user ? {
      userId: user.userId,
      email: user.email,
      name: user.name,
    } : undefined
  });

  const updateMutation = useMutation({
    mutationFn: profileService.updateProfile,
    onSuccess: (_, updatedName) => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setUser(prev => prev ? { ...prev, name: updatedName } : null);
      setIsEditing(false);
      toast({
        title: 'Success',
        description: 'Profile updated successfully',
      });
    },
  });
  
  const cancelSubscriptionMutation = useMutation({
    mutationFn: subscriptionService.cancelSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credits'] });
      refetchCredits();
      setCancelDialog(false);
      toast({
        title: 'Subscription Cancelled',
        description: 'Your subscription will end at the current billing period.',
      });
    }
  });
  
  const deleteMutation = useMutation({
    mutationFn: profileService.deleteAccount,
    onSuccess: () => {
      navigate('/');
      toast({
        title: 'Account Deleted',
        description: 'Your account has been successfully deleted.',
      });
    },
  });

  useEffect(() => {
    if (profile) {
      setName(profile.name);
    }
  }, [profile]);

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync(name);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update profile',
        variant: 'destructive',
      });
    }
  };
  
  const handleCancelSubscription = async () => {
    try {
      await cancelSubscriptionMutation.mutateAsync();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to cancel subscription',
        variant: 'destructive',
      });
    }
  };
  
  // Format subscription status for display
  const formatSubscriptionStatus = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>;
      case 'canceled':
        return <Badge variant="destructive">Canceled</Badge>;
      case 'past_due':
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Past Due</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };
  
  // Calculate the percentage used (total used out of available + used)
  const calculateUsage = () => {
    if (!creditInfo) return 0;
    const total = creditInfo.available_credits + creditInfo.total_used;
    if (total === 0) return 0;
    return (creditInfo.total_used / total) * 100;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="container max-w-5xl py-6 md:py-8 px-4 md:px-6"
    >
      {/* Mobile-friendly top nav */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-sm text-muted-foreground hover:text-foreground flex items-center md:hidden"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back
        </button>
        
        {/* Only show on desktop */}
        <Button
          variant="ghost"
          className="mb-6 -ml-2 hidden md:flex"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>
      </div>

      <div className="flex flex-col gap-6 md:gap-8">
        <div className="space-y-2 md:space-y-3">
          <h1 className="text-2xl md:text-4xl font-bold">Profile Settings</h1>
          <p className="text-base md:text-lg text-muted-foreground">
            Manage your account settings and subscription preferences
          </p>
        </div>

        <div className="grid gap-4 md:gap-6">
          {/* Profile Information */}
          <Card className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg md:text-xl font-semibold">Profile Information</h2>
              </div>
              <Button 
                variant="ghost" 
                onClick={() => setIsEditing(!isEditing)}
                className="text-sm md:text-base"
              >
                {isEditing ? 'Cancel' : 'Edit'}
              </Button>
            </div>

            <div className="grid gap-4">
              <div className="space-y-2">
                <Label className="text-sm md:text-base">Name</Label>
                {isEditing ? (
                  <Input 
                    value={name} 
                    onChange={(e) => setName(e.target.value)}
                    className="h-10"
                  />
                ) : (
                  <p className="text-sm md:text-base text-muted-foreground">{user?.name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-sm md:text-base">Email</Label>
                <p className="text-sm md:text-base text-muted-foreground">{user?.email}</p>
              </div>
              {isEditing && (
                <Button 
                  className="w-full mt-2"
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              )}
            </div>
          </Card>

          {/* Subscription & Credits */}
          <Card className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-0 mb-4">
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg md:text-xl font-semibold">Subscription & Credits</h2>
              </div>
              
              {creditInfo && (
                <div className="flex items-center gap-2 ml-7 md:ml-0">
                  <Badge variant="outline" className="capitalize text-sm">
                    {creditInfo.tier} Plan
                  </Badge>
                  {creditInfo.subscription_status !== 'none' && 
                   formatSubscriptionStatus(creditInfo.subscription_status)}
                </div>
              )}
            </div>

            {creditsLoading ? (
              <div className="flex justify-center p-6 md:p-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : creditInfo ? (
              <div className="space-y-4 md:space-y-6">
                {creditInfo.cancel_at_period_end && creditInfo.current_period_end && (
                  <div className="bg-amber-50 border border-amber-200 p-3 rounded-md flex items-start text-sm md:text-base">
                    <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      Your subscription will end on {format(new Date(creditInfo.current_period_end), 'MMMM d, yyyy')}.
                      You'll still have access to all features until then.
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div className="bg-muted p-3 md:p-4 rounded-lg space-y-1 md:space-y-2">
                    <div className="text-sm text-muted-foreground">Available Credits</div>
                    <div className="text-2xl md:text-3xl font-bold">{creditInfo.available_credits}</div>
                    {creditInfo.next_refresh && (
                      <div className="flex items-center text-xs md:text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3 mr-1" />
                        Refreshes on {format(new Date(creditInfo.next_refresh), 'MMM d, yyyy')}
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-muted p-3 md:p-4 rounded-lg space-y-1 md:space-y-2">
                    <div className="text-sm text-muted-foreground">Total Credits Used</div>
                    <div className="text-2xl md:text-3xl font-bold">{creditInfo.total_used}</div>
                  </div>
                </div>
                
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span>Usage</span>
                    <span>{Math.round(calculateUsage())}%</span>
                  </div>
                  <Progress value={calculateUsage()} className="h-2" />
                </div>
                
                {creditInfo.usage_history && creditInfo.usage_history.length > 0 && (
                  <div className="border-t pt-4 mt-4">
                    <h3 className="font-medium mb-3 text-sm md:text-base">Recent Credit Usage</h3>
                    <div className="space-y-2">
                      {creditInfo.usage_history.slice(0, 5).map(usage => (
                        <div key={usage.id} className="flex justify-between items-center p-2 md:p-3 bg-muted rounded-md">
                          <div>
                            <div className="font-medium text-sm">{usage.description}</div>
                            <div className="text-xs text-muted-foreground">
                              {format(new Date(usage.date), 'MMM d, yyyy, h:mm a')}
                            </div>
                          </div>
                          <Badge variant="secondary" className="text-xs md:text-sm">{usage.credits} credits</Badge>
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" className="w-full mt-2 text-sm">
                        View all usage history
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
                
                <div className="pt-4 border-t flex flex-col gap-2">
                  <Button
                    onClick={() => navigate('/pricing')}
                    variant={creditInfo.tier === 'free' ? 'default' : 'outline'}
                    className="w-full"
                  >
                    {creditInfo.tier === 'free' ? 'Upgrade Plan' : 'Change Plan'}
                  </Button>
                  
                  {/* Show cancel button only for paid subscriptions that aren't already cancelled */}
                  {creditInfo.tier !== 'free' && !creditInfo.cancel_at_period_end && (
                    <Button
                      onClick={() => setCancelDialog(true)}
                      variant="destructive"
                      className="w-full"
                    >
                      Cancel Subscription
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No subscription information available</p>
                <Button className="mt-4" onClick={() => navigate('/pricing')}>
                  View Pricing Plans
                </Button>
              </div>
            )}
          </Card>

          {/* Security Settings */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="h-5 w-5 text-indigo-600" />
              <h2 className="text-xl font-semibold">Security Settings</h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-3">
                <div>
                  <h3 className="font-medium">Change Password</h3>
                  <p className="text-sm text-muted-foreground">Update your account password</p>
                </div>
                <Button variant="outline" onClick={() => {
                  toast({
                    title: "Coming Soon",
                    description: "Password change functionality will be available soon. We're still in beta and actively building new features.",
                  });
                }}>
                  Change Password
                </Button>
              </div>

              <div className="flex items-center justify-between py-3">
                <div>
                  <h3 className="font-medium">Two-Factor Authentication</h3>
                  <p className="text-sm text-muted-foreground">Add an extra layer of security</p>
                </div>
                <Button variant="outline" onClick={() => {
                  toast({
                    title: "Coming Soon",
                    description: "Two-factor authentication will be available soon. We're still in beta and actively building new features.",
                  });
                }}>
                  Enable 2FA
                </Button>
              </div>

              <div className="flex items-center justify-between py-3 border-t">
                <div className="flex-1 mr-4">
                  <h3 className="font-medium text-red-600">Delete Account</h3>
                  <p className="text-sm text-muted-foreground">Delete account permanently</p>
                </div>
                <Button 
                  variant="destructive"
                  onClick={() => {
                    toast({
                      title: "Coming Soon",
                      description: "Account deletion will be available soon. We're still in beta and actively building new features. Please contact support if you need immediate assistance.",
                    });
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
      
      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription? You'll continue to have access to your current plan until the end of your billing period.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(false)}>
              Keep Subscription
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => cancelSubscriptionMutation.mutate()}
              disabled={cancelSubscriptionMutation.isPending}
            >
              {cancelSubscriptionMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Yes, Cancel'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
} 